import { useState } from "react";
import { useTitanApp } from "@/hooks/useTitanApp";
import { useNavigate } from "react-router";
import {
  nhsAhtWeightTemplateId,
  nhsAhtTravelHealthTemplateId,
} from "@/data/constantStrings";
import {
  setCurrentGlobalTabText,
  setCurrentGlobalTabValue,
  setisPatientSearched,
  setPatientInfoTab,
} from "../../dispatchers/appDispatchers";
import { setIsPatientDashboard } from "../../dispatchers/patientDispatchers";
import { useEffect } from "react";
import {
  getDefaultCompanyServices,
  setShowNHS111ReferralTab,
} from "../../dispatchers/pharmacyServiceDispatchers";
import {
  getPatientConsultationByConsultationId,
  setPreConsultationTemplate,
  setPreConsultationTranscription,
  getPreConsultationByConsultationId,
  getPaymentAuditByConsultation,
  setPatientTabSwitcher,
  setConsultationId,
  getPatientByPatientId,
  addPatientConsultation,
  setFreeTypeMode,
  setIsFinalSummary,
  setPatientNotes,
  setSignatureDetails,
  setTranscript,
  setConsultationPaused,
  setCurrentConsultationMediaButtonMode,
  setIsConsultationEnabled,
  setPatientTranscription,
  setSelectedPatientConsultation,
  setShowPlayPauseBtn,
  setAiChatSuccessMessages,
  setAddPatientAIChatMessage,
  setisHandleStartConsultation,
  updateConsultationStatus,
  updateEventStatus,
  setPatientAIChatMessages,
  setVaccineDetails,
  updateConsultationTemplate,
  setPaymentAudit,
  setSnomedCodes,
  getPatientConsultations,
} from "@/dispatchers/patientDispatchers";
import {
  setIsTemplateSelected,
  setSelectedTemplate,
  setStreamedText,
  sendAIQAService,
} from "@/dispatchers/templateDispatchers";
import { ConsultationButtonStatus } from "@/enum/ConsultationButtonStatus";
import { getCompanyDocumentsByCompanyId } from "@/dispatchers/companyDocumentsDispatchers";
import { extractSnomedCodesFromSummary } from "@/utils/extractSnomedCodesFromSummary";

interface Appointment {
  id: string;
  patientName: string;
  patientPhone?: string;
  service: string;
  date: Date;
  time: string;
  duration: string;
  status: string;
  pharmacist?: string;
  preConsultationTemplateId?: string;
  patientEmail?: string;
  patientID?: string;
}

interface Alert {
  id: string;
  type: string;
  title: string;
  message: string;
  time: string;
}

interface MissedAppointment {
  id: string;
  patientName: string;
  service: string;
  scheduledDate: string;
  scheduledTime: string;
  reason: string;
}

// Mock data
const alerts: Alert[] = [];

// Interface for active consultations
interface ActiveConsultation {
  id: string;
  patientName: string;
  consultationType: string;
  date: string;
  time: string;
  status: string;
}

const templateServiceNameMap: Record<string, string> = {
  [nhsAhtWeightTemplateId]: "Weight Management (AHT)",
  [nhsAhtTravelHealthTemplateId]: "Travel Health (AHT)",
};

// Function to transform API consultation data to dashboard format
const transformApiConsultations = (
  apiData: any[] | null,
): ActiveConsultation[] => {
  if (!apiData) return [];

  const getEffectiveDate = (c: any): string =>
    c.startedOn && c.createdOn && new Date(c.startedOn) > new Date(c.createdOn)
      ? c.startedOn
      : c.createdOn;

  return [...apiData]
    .sort((a, b) => {
      return (
        new Date(getEffectiveDate(b)).getTime() -
        new Date(getEffectiveDate(a)).getTime()
      );
    })
    .map((consultation: any) => {
      const createdDate = new Date(getEffectiveDate(consultation));

      const dateString = createdDate.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });

      const timeString = createdDate.toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });

      const statusMap: { [key: number]: string } = {
        1: "completed",
        2: "active",
        3: "paused",
        4: "pending",
        5: "cancel",
      };

      return {
        id: consultation.consultationId || "",
        patientName: consultation.patientName || "",
        consultationType: consultation.serviceName || templateServiceNameMap[consultation.templateId] || consultation.templateName || "",
        date: dateString,
        time: timeString,
        status: statusMap[consultation.consultationStatus] || "pending",
      };
    });
};

// Function to transform API notification data to dashboard format
const transformApiNotifications = (apiData: any[] | null): any[] => {
  if (!apiData) return [];

  return [...apiData]
    .sort((a, b) => {
      return new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime();
    })
    .map((notification: any) => {
      const severityMap: { [key: number]: string } = {
        1: "info",
        2: "warning",
        3: "critical",
      };

      return {
        id: notification._id || "",
        type: "alert",
        severity: severityMap[notification.notificationStatusType] || "info",
        title: notification.title || "Notification",
        message: notification.message || "",
        time: notification.timeAgo || "",
        createdOn: notification.createdOn,
      };
    });
};

const hasUsableTemplate = (consultation: any): boolean => {
  const template = consultation?.template;
  if (!template || typeof template !== "object") return false;

  const hasTemplateId = Boolean(
    consultation?.templateId || template?.templateId || template?._id,
  );
  const hasTemplateName = Boolean(
    template?.templateName && String(template.templateName).trim(),
  );
  const hasTemplateSections =
    Array.isArray(template?.templateSections) &&
    template.templateSections.length > 0;

  return hasTemplateId || hasTemplateName || hasTemplateSections;
};

interface Resource {
  CalenderName: string;
  eventId: string;
  EventId: string;
  StartDate: string;
  EndDate: string;
  Duration: number;
  DurationUnit?: string;
  PatientName: string;
  EventStatus: number;
  ClinicalServiceID?: string;
  Name?: string;
  ServiceID?: string;
  Mobile?: string;
  PreConsultationTemplateId?: string;
  Email?: string;
  PatientID?: string;
}

export const useDashboardData = () => {
  const {
    appState,
    pharmacyServiceState,
    pharmacyDashboardState,
  } = useTitanApp();
  const routerNavigate = useNavigate();
  const {
    darkMode,
    currentPharmacyId,
    currentUser,
    currentTeam,
    dashboardTeams,
  } = appState || {};
  const {
    isPharmacyDashboardLoading = false,
    pharmacyDashboardData = null,
    userScheduleEventsData = null,
    userActiveConsultationsData = null,
    pharmacyNotificationsData = null,
    isPharmacyNotificationsLoading = false,
    isPharmacyScheduleLoading = false,
    isActiveConsultationsLoading = false,
    ifPharmacyDashboardEnabled,
  } = pharmacyDashboardState || {};
  const { defaultPharmacyServices = [], isPharmacyServiceLoading = false } =
    pharmacyServiceState || {};
  const { companyId } = appState || {};

  useEffect(() => {
    if (
      (!defaultPharmacyServices || defaultPharmacyServices.length === 0) &&
      !isPharmacyServiceLoading &&
      companyId
    ) {
      getDefaultCompanyServices(companyId);
    }
  }, [defaultPharmacyServices, isPharmacyServiceLoading, companyId]);

  const [selectedDate, setSelectedDate] = useState(new Date());
  // Process Dashboard KPIs
  const kpis = (pharmacyDashboardData as any)?.kpis || [];
  const patientKpi = kpis.find((k: any) => k.pharmacyDashboardType === 1);
  const serviceKpi = kpis.find((k: any) => k.pharmacyDashboardType === 2);

  const kpiStats = {
    activePatients: {
      value: patientKpi?.value || 0,
      percentage: patientKpi?.subText || "0",
    },
    activeServices: {
      value: serviceKpi?.value || 0,
      percentage: serviceKpi?.subText || "0",
    },
  };
  // Date navigation functions
  const goToPreviousDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(selectedDate.getDate() - 1);
    setSelectedDate(newDate);
  };

  const goToNextDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(selectedDate.getDate() + 1);
    setSelectedDate(newDate);
  };

  const goToToday = () => {
    setSelectedDate(new Date());
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const formatSelectedDate = (date: Date) => {
    if (isToday(date)) {
      return "Today";
    }
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === yesterday.toDateString()) {
      return "Yesterday";
    }
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    if (date.toDateString() === tomorrow.toDateString()) {
      return "Tomorrow";
    }
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatDateYYYYMMDD = (date: Date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  const parseLocalDateTime = (value: string) => {
    const match = value.match(
      /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/,
    );
    if (!match) {
      return new Date(value);
    }
    const [, y, m, d, hh, mm, ss] = match;
    return new Date(
      Number(y),
      Number(m) - 1,
      Number(d),
      Number(hh),
      Number(mm),
      Number(ss || 0),
    );
  };
  // Transform dynamic schedule events
  const rawAppointments: Appointment[] =
    (userScheduleEventsData as Resource[])?.map((resource) => {
      const date = parseLocalDateTime(resource.StartDate);

      // Map EventStatus to status string
      let status = "pending";
      switch (resource.EventStatus) {
        case 1:
          status = "completed";
          break;
        case 2:
          status = "pending";
          break;
        case 3:
          status = "active";
          break;
        case 4:
          status = "rescheduled";
          break;
        case 5:
          status = "no show";
          break;
        default:
          status = "pending";
      }

      if (status === "pending" && date < new Date()) {
        status = "overdue";
      }

      // Map ServiceID to service name
      const serviceObj = defaultPharmacyServices?.find(
        (s: any) => s._id === resource?.ServiceID,
      );
      const serviceName = serviceObj?.serviceName || "General Consultation";
      return {
        id: resource.EventId,
        patientName:
          resource.PatientName && resource.PatientName.trim() !== ""
            ? resource.PatientName
            : resource.Name || "",
        service: serviceName,
        date: date,
        time: date
          .toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
          })
          .toLowerCase(),
        duration: `${resource.Duration}${resource.DurationUnit || "m"}`,
        status: status,
        pharmacist: resource.CalenderName,
        patientPhone: resource.Mobile || undefined,
        preConsultationTemplateId: resource.PreConsultationTemplateId || undefined,
        patientEmail: resource.Email || undefined,
        patientID: resource.PatientID || undefined,
      };
    }) || [];
  // Calculate dynamic missed appointments (Pending status + Past date)
  const activeMissedAppointments: MissedAppointment[] = rawAppointments
    .filter((apt) => apt.status === "pending" && apt.date < new Date())
    .map((apt) => ({
      id: apt.id,
      patientName: apt.patientName,
      service: apt.service,
      scheduledDate: formatDateYYYYMMDD(apt.date),
      scheduledTime: apt.time,
      reason: "No show",
    }));

  // Filter raw appointments to only show current date and future appointments
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);

  const allAppointments = rawAppointments?.filter(
    (apt) => apt.date >= todayStart,
  );

  // Filter appointments for selected date — all appointments remain visible for the entire day
  const selectedDateAppointments = allAppointments?.filter((appointment) => {
    return appointment.date.toDateString() === selectedDate?.toDateString();
  });

  // Navigate to the calendar targeted at a specific appointment's date/day
  // view, so the calendar opens showing exactly where that appointment is
  // instead of its default view.
  const onAppointmentClick = (date: Date | string, appointmentId?: string) => {
    const dateObj = typeof date === "string" ? new Date(date) : date;
    const dateString = formatDateYYYYMMDD(dateObj);

    try {
      // calendarFromAppointment tells scheduleEventsLoader to skip fetching
      // the user's default calendar view (we're forcing day view here), and
      // Calendar/Schedule.tsx to force day view at calendarRequestedDate
      // instead of applying that default. Kept out of the URL so the address
      // bar stays clean.
      localStorage.setItem("calendarFromAppointment", "1");
      localStorage.setItem("calendarRequestedDate", dateString);
      localStorage.setItem("calendarRequestedView", "day");
      if (appointmentId) {
        localStorage.setItem("calendarRequestedAppointmentId", appointmentId);
      } else {
        localStorage.removeItem("calendarRequestedAppointmentId");
      }
    } catch {}

    // Logic from MiniCalendarControl to compute tab index
    const { features } = appState || {};
    const hasDashboard = !!features?.ifPharmacyDashboardEnabled;
    const hasWorkforce = !!features?.ifWorkForceTabEnabled;

    let targetIndex = 0;
    if (hasDashboard) targetIndex++;
    targetIndex++; // Services
    if (hasWorkforce) targetIndex++;
    // Calendar is at the next position

    setCurrentGlobalTabValue(targetIndex);
    setCurrentGlobalTabText("Calendar");
    setisPatientSearched(false);
    setIsPatientDashboard(false);

    if (currentPharmacyId) {
      const targetRoute = features?.ifNewCalendar
        ? `/pharmacy/schedule-new/${currentPharmacyId}`
        : `/pharmacy/schedule/${currentPharmacyId}`;
      routerNavigate(targetRoute);
    }
  };

  // State for pagination
  const [consultationsPage, setConsultationsPage] = useState(1);
  const [missedAppointmentsPage, setMissedAppointmentsPage] = useState(1);
  const [alertsPage, setAlertsPage] = useState(1);
  const [notificationsPage, setNotificationsPage] = useState(1);

  const consultationsItemsPerPage = 8;
  const missedItemsPerPage = 3;
  const alertsItemsPerPage = 3;
  const notificationsItemsPerPage = 8;

  // Transform API consultation data to dashboard format
  const activeConsultations = transformApiConsultations(
    userActiveConsultationsData,
  );

  // Pagination for consultations
  const totalConsultations = activeConsultations?.length;
  const totalPages = Math.ceil(totalConsultations / consultationsItemsPerPage);
  const startIndex = (consultationsPage - 1) * consultationsItemsPerPage;
  const endIndex = startIndex + consultationsItemsPerPage;
  const paginatedConsultations = activeConsultations?.slice(
    startIndex,
    endIndex,
  );

  // Pagination for missed appointments
  const totalMissedAppointments = activeMissedAppointments?.length;
  const totalMissedPages = Math.ceil(
    totalMissedAppointments / missedItemsPerPage,
  );
  const missedStartIndex = (missedAppointmentsPage - 1) * missedItemsPerPage;
  const missedEndIndex = missedStartIndex + missedItemsPerPage;
  const paginatedMissedAppointments = activeMissedAppointments?.slice(
    missedStartIndex,
    missedEndIndex,
  );

  // Pagination for alerts
  const totalAlerts = alerts.length;
  const totalAlertsPages = Math.ceil(totalAlerts / alertsItemsPerPage);
  const alertsStartIndex = (alertsPage - 1) * alertsItemsPerPage;
  const alertsEndIndex = alertsStartIndex + alertsItemsPerPage;
  const paginatedAlerts = alerts.slice(alertsStartIndex, alertsEndIndex);

  // Notifications Transformation and Pagination
  const allNotifications = transformApiNotifications(pharmacyNotificationsData);
  const totalNotifications = allNotifications.length;
  const totalNotificationsPages = Math.ceil(
    totalNotifications / notificationsItemsPerPage,
  );
  const notificationsStartIndex =
    (notificationsPage - 1) * notificationsItemsPerPage;
  const notificationsEndIndex =
    notificationsStartIndex + notificationsItemsPerPage;
  const paginatedNotifications = allNotifications.slice(
    notificationsStartIndex,
    notificationsEndIndex,
  );
  const handleConsultationClick = async (consultation) => {
    setCurrentGlobalTabValue(ifPharmacyDashboardEnabled ? 4 : 4);
    setCurrentGlobalTabText("Patients");
    setisPatientSearched(true);
    setPatientInfoTab(1);
    const consultationRes = await getPatientConsultationByConsultationId(
      consultation.id,
    );
    setIsTemplateSelected(hasUsableTemplate(consultationRes));
    setConsultationId(consultation.id);
    await getPatientByPatientId(consultationRes?.patientId);
    const patientConsultation = await getPatientConsultations(
      consultationRes?.patientId,
    );
    setPatientTabSwitcher(consultationRes?.preConsultationTemplate ? 1 : 0);
    await handleArrowClick(consultationRes);
  };

  const handleArrowClick = async (consultationData) => {
    setFreeTypeMode(false);
    setIsConsultationEnabled(false);
    setSelectedPatientConsultation(consultationData);
    setConsultationId(consultationData?.consultationId);
    setPatientTranscription(consultationData?.transcription);
    setTranscript(consultationData?.transcription);
    setSelectedTemplate(consultationData?.template);
    setSignatureDetails(consultationData?.patientConsent);
    setIsFinalSummary(consultationData?.notes === null ? false : true);
    setPatientNotes(consultationData?.notes ?? "");
    setShowPlayPauseBtn(consultationData?.consultationStatus === 3);
    const { mainSummary, snomedCodes } = extractSnomedCodesFromSummary(
      consultationData?.consultationSummary,
    );

    setStreamedText(mainSummary);
    setSnomedCodes(snomedCodes);
    setConsultationPaused(
      consultationData?.consultationStatus === 3 &&
        !!consultationData?.transcription?.trim()
        ? true
        : false,
    );

    if (consultationData?.template?.linkedDocumentId && companyId) {
      getCompanyDocumentsByCompanyId(companyId);
    }

    await getPaymentAuditByConsultation({
      consultationID: consultationData?.consultationId,
    });
    setisHandleStartConsultation(false);
    if (
      hasUsableTemplate(consultationData) &&
      (consultationData?.consultationStatus === 2 ||
        consultationData?.consultationStatus === 3)
    ) {
      setIsTemplateSelected(true);
    }
    if (
      consultationData?.consultationButtonStatus ===
      ConsultationButtonStatus.Manual
    ) {
      setCurrentConsultationMediaButtonMode("manual");
    }
    if (consultationData?.preConsultationTemplate) {
      const preConsultDataRes = await getPreConsultationByConsultationId({
        consultationId: consultationData?.consultationId,
      });
      setPreConsultationTemplate(preConsultDataRes?.preConsultationTemplate);
      if (preConsultDataRes?.preConsultationTemplate) {
        let preConsultTranscription = "";
        preConsultDataRes?.preConsultationTemplate?.templateSections?.forEach(
          (section) => {
            section?.userControls?.forEach((control) => {
              if (control.label && control.value) {
                preConsultTranscription += ` ${control.label} ${control.value} `;
              }
            });
          },
        );

        setPreConsultationTranscription(preConsultTranscription.trim());
      } else {
        setPreConsultationTranscription("");
      }
    } else {
      setPreConsultationTemplate(null);
      setPreConsultationTranscription("");
    }

    routerNavigate(
      `/pharmacy/patientconsultation/${currentPharmacyId}/${consultationData?.patientId}/${consultationData.consultationId}`,
    );
    setPatientInfoTab(1);
    setShowNHS111ReferralTab(false);
    setPatientTabSwitcher(consultationData?.preConsultationTemplate ? 1 : 0);
    if (consultationData?.consultationChat) {
      try {
        const parsedAiChatMessages = JSON.parse(
          consultationData.consultationChat,
        );
        setAiChatSuccessMessages();
        parsedAiChatMessages.forEach((message) => {
          setAddPatientAIChatMessage(message);
        });
      } catch (err) {
        console.error("Invalid consultationChat JSON:", err);
      }
    }
    const filledCount = countFilledControls(consultationData?.template);
    if (filledCount < 1 && consultationData?.transcription?.trim()) {
      const allUserControls =
        consultationData?.template?.templateSections?.flatMap(
          (sec) => sec.userControls,
        ) || [];

      const questions = allUserControls.map((ctrl, idx) => ({
        [`q${idx + 1}`]: ctrl.label,
      }));
      const qaResponse = await sendAIQAService({
        q: questions,
        t: consultationData?.transcription,
      });
      const updatedTemplate = fillTemplateWithQA(
        consultationData?.template,
        qaResponse,
      );
      setSelectedTemplate(updatedTemplate);
      if (updatedTemplate?.templateSections?.length) {
        await updateConsultationTemplate({
          consultationId: consultationData?.consultationId,
          payload: updatedTemplate.templateSections,
        });
      }
    } else {
      setSelectedTemplate(consultationData?.template);
    }
  };
  function countFilledControls(template) {
    let count = 0;
    template?.templateSections?.forEach((section) => {
      section.userControls.forEach((ctrl) => {
        if (ctrl.value && ctrl.value?.trim() !== "") count++;
      });
    });
    return count;
  }

  function fillTemplateWithQA(template, qaResponse) {
    let answers = {};
    try {
      answers = JSON.parse(qaResponse?.response || "{}");
    } catch (e) {
      console.error("Invalid QA response JSON", e);
    }

    let counter = 1;
    const updatedSections = template?.templateSections?.map((section) => ({
      ...section,
      userControls: section?.userControls?.map((ctrl) => {
        const key = `a${counter}`;
        const newValue =
          answers.hasOwnProperty(key) && answers[key] !== null
            ? answers[key]
            : ctrl.value;
        counter++;
        return { ...ctrl, value: newValue };
      }),
    }));

    return {
      ...template,
      templateSections: updatedSections,
    };
  }

  return {
    selectedDate,
    goToPreviousDay,
    goToNextDay,
    goToToday,
    isToday,
    formatSelectedDate,
    formatDateYYYYMMDD,
    selectedDateAppointments,
    thisWeekAppointments: allAppointments,
    alerts,
    missedAppointments: activeMissedAppointments,
    isDarkMode: darkMode,
    onAppointmentClick,
    isPharmacyDashboardLoading,
    isPharmacyNotificationsLoading,
    isPharmacyScheduleLoading,
    isActiveConsultationsLoading,
    kpiStats,
    // New pagination return values
    paginatedConsultations,
    totalConsultations,
    consultationsPage,
    setConsultationsPage,
    totalPages,
    startIndex,
    endIndex,
    paginatedMissedAppointments,
    totalMissedAppointments,
    missedAppointmentsPage,
    setMissedAppointmentsPage,
    totalMissedPages,
    missedStartIndex,
    missedEndIndex,
    paginatedAlerts,
    totalAlerts,
    alertsPage,
    setAlertsPage,
    totalAlertsPages,
    alertsStartIndex,
    alertsEndIndex,
    // Notifications pagination
    paginatedNotifications,
    totalNotifications,
    notificationsPage,
    setNotificationsPage,
    totalNotificationsPages,
    notificationsStartIndex,
    notificationsEndIndex,
    currentUser,
    currentPharmacyId,
    handleConsultationClick,
    currentTeamMembers: dashboardTeams?.teamMembers || [],
  };
};
